import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Globe, Leaf, FlaskConical, BadgeCheck, Sprout, Handshake, ArrowRight } from 'lucide-react';
import { useContent } from '../lib/ContentContext';
import { assetUrl } from '../lib/api';
import { HeroSlider } from '../components/site/HeroSlider';
import { SectionHeading } from '../components/site/SectionHeading';
import { NewsCards } from '../components/site/NewsCards';
import { ImageLightbox } from '../components/site/ImageLightbox';

const ICONS = { Globe, Leaf, FlaskConical, BadgeCheck, Sprout, Handshake };

export default function Home() {
  const { content } = useContent();
  const [aboutLightboxIndex, setAboutLightboxIndex] = useState(null);
  if (!content) return null;

  const intro = content.home_intro || {};
  const industries = content.home_industries || {};
  const about = content.home_about || {};
  const partners = content.home_partners || {};
  const why = content.home_why || {};
  const solutions = content.home_solutions || {};
  const newsHeader = content.home_news_header || {};
  const homeContact = content.home_contact || {};
  const aboutImages = about.images && about.images.length > 0
    ? about.images
    : [{ image: about.image, caption: about.caption }];

  return (
    <div>
      {/* HERO SLIDER */}
      <HeroSlider slides={content.hero_slides?.slides || []} />

      {/* INTRO / CERTIFICATIONS */}
      <section className="py-14 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <h1 className="text-center text-[#26247B] font-extrabold text-2xl sm:text-3xl tracking-tight">{intro.iso_line}</h1>
          <h2 className="mt-5 text-center text-[#2E8B57] font-extrabold text-3xl sm:text-4xl tracking-tight max-w-5xl mx-auto leading-snug">
            {intro.headline}
          </h2>
          <p
            className="cms-html mt-6 max-w-4xl mx-auto text-center text-black/80 leading-8"
            dangerouslySetInnerHTML={{ __html: intro.paragraph }}
          />

          <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-8 sm:gap-10 max-w-5xl mx-auto">
            {(intro.features || []).map((f, i) => {
              const Icon = ICONS[f.icon] || Globe;
              return (
                <div key={i} className="text-center">
                  <Icon className="h-11 w-11 text-[#26247B] mx-auto" strokeWidth={1.8} />
                  <h3 className="mt-4 text-[#2E8B57] font-bold text-lg">{f.title}</h3>
                  <p className="mt-2 text-black/80 text-sm leading-6">{f.text}</p>
                </div>
              );
            })}
          </div>

          <div className="mt-12 text-center">
            <Link
              to={intro.cta_link || '/about-us'}
              data-testid="home-intro-learn-more-button"
              className="inline-flex items-center gap-2 rounded-md bg-[#26247B] text-white px-7 py-3.5 font-bold transition-colors duration-200 hover:bg-[#1f1d66]"
            >
              {intro.cta_text} <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* INDUSTRIES WE SERVE */}
      <section className="py-14 sm:py-20 bg-[#F6F7F9]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <SectionHeading>{industries.title}</SectionHeading>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {(industries.cards || []).map((card, i) => (
              <div key={i} data-testid="home-industries-card" className="bg-white rounded-2xl shadow-[0_8px_20px_rgba(17,24,39,0.1)] overflow-hidden card-lift">
                <div className="p-5 pb-0">
                  <img src={assetUrl(card.image)} alt={card.title} className="w-full h-[150px] object-cover rounded-lg" loading="lazy" />
                </div>
                <div className="p-6 text-center">
                  <h3 className="text-[#2E8B57] font-bold text-xl">{card.title}</h3>
                  <p className="mt-2.5 text-black/80 text-sm leading-6">{card.text}</p>
                  <Link
                    to={card.link}
                    data-testid="home-industries-learn-more-link"
                    className="mt-4 inline-flex text-[#2E8B57] font-bold text-sm hover:underline"
                  >
                    Learn more…
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT INTEXSO */}
      <section className="py-14 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-stretch">
            <div>
              <h2 className="text-[#2E8B57] font-extrabold text-3xl sm:text-4xl tracking-tight">{about.title}</h2>
              <p className="mt-3 italic font-bold text-[#26247B] text-lg">{about.tagline}</p>
              <div className="mt-6 space-y-4">
                {(about.paragraphs || []).map((p, i) => (
                  <p key={i} className="cms-html text-black/80 leading-8" dangerouslySetInnerHTML={{ __html: p }} />
                ))}
              </div>
            </div>
            <div className="flex flex-col gap-6 lg:h-full">
              {aboutImages.map((img, i) => (
                <div key={i} className="flex flex-col lg:flex-1 lg:min-h-0">
                  <button
                    type="button"
                    data-testid={`home-about-image-${i}`}
                    aria-label={`Enlarge image: ${img.caption || 'Intexso facility'}`}
                    onClick={() => setAboutLightboxIndex(i)}
                    className="group relative overflow-hidden rounded-2xl shadow-[0_10px_30px_rgba(17,24,39,0.12)] aspect-[4/3] lg:aspect-auto lg:h-full focus:outline-none focus-visible:ring-2 focus-visible:ring-[#F4A300] focus-visible:ring-offset-2"
                  >
                    <img
                      src={assetUrl(img.image)}
                      alt={img.caption || 'Intexso facility'}
                      className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-105"
                      loading="lazy"
                    />
                    <span className="absolute inset-0 bg-[#26247B]/0 transition-colors duration-200 group-hover:bg-[#26247B]/15" />
                  </button>
                  <p className="mt-3 text-sm text-black/70 text-center font-medium">{img.caption}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <ImageLightbox
        open={aboutLightboxIndex !== null}
        onOpenChange={(o) => !o && setAboutLightboxIndex(null)}
        src={aboutLightboxIndex !== null ? assetUrl(aboutImages[aboutLightboxIndex].image) : ''}
        alt={aboutLightboxIndex !== null ? (aboutImages[aboutLightboxIndex].caption || 'Intexso facility') : ''}
      />

      {/* GLOBAL PARTNERS */}
      <section id="global-partners" className="py-14 sm:py-20 bg-[#F6F7F9]" data-testid="home-partners-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <SectionHeading subtitle={partners.subtitle}>{partners.title}</SectionHeading>
          <div className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
            {(partners.cards || []).map((card, i) => (
              <div
                key={i}
                data-testid="home-partner-card"
                className="bg-white rounded-2xl shadow-[0_8px_20px_rgba(17,24,39,0.1)] px-8 py-10 flex flex-col items-center text-center card-lift"
              >
                <img
                  src={assetUrl(card.logo)}
                  alt={card.heading}
                  data-testid="home-partner-logo"
                  className="w-auto max-w-[220px] object-contain"
                  style={{ height: `${56 * (card.logo_scale || 1)}px` }}
                  loading="lazy"
                />
                <span className="mt-6 block h-1 w-12 rounded-full bg-[#F4A300]" aria-hidden="true" />
                <h3 className="mt-6 text-[#26247B] font-extrabold text-lg leading-snug" data-testid="home-partner-heading">
                  {card.heading}
                </h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE */}
      <section className="py-14 sm:py-20 bg-[#F6F7F9]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <SectionHeading>{why.title}</SectionHeading>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {(why.items || []).map((item, i) => {
              const Icon = ICONS[item.icon] || BadgeCheck;
              return (
                <div key={i} className="flex gap-4">
                  <Icon className="h-9 w-9 text-[#F4A300] shrink-0" strokeWidth={1.8} />
                  <div>
                    <h3 className="text-[#26247B] font-extrabold text-lg leading-snug">{item.title}</h3>
                    <p className="mt-2 text-[#2E8B57] font-semibold text-sm leading-6">{item.text}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SOLUTIONS */}
      <section className="py-14 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <SectionHeading subtitle={solutions.intro}>{solutions.title}</SectionHeading>
          {solutions.subheading && (
            <div className="mt-10 max-w-4xl mx-auto text-center">
              <h3 className="text-[#26247B] font-extrabold text-xl sm:text-2xl">{solutions.subheading}</h3>
              {solutions.paragraph && <p className="mt-4 text-black/80 leading-8">{solutions.paragraph}</p>}
            </div>
          )}
          {solutions.industries_title && (
            <h4 className="mt-12 text-center text-[#2E8B57] font-extrabold text-lg uppercase tracking-wide">{solutions.industries_title}</h4>
          )}
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
            {(solutions.cards || []).map((card, i) => (
              <div
                key={i}
                className={`px-6 py-6 ${i > 0 ? 'lg:border-l lg:border-[#F4A300]' : ''} ${i > 0 ? 'border-t lg:border-t-0 border-[#F4A300]/40' : ''}`}
              >
                <img src={assetUrl(card.icon_image)} alt="" className="h-24 w-auto object-contain mx-auto" loading="lazy" />
                <h3 className="mt-5 text-[#26247B] font-extrabold text-lg text-center leading-snug">{card.title}</h3>
                {card.text && (
                  <p className="mt-3 text-black/80 text-sm leading-6 text-center cms-html" dangerouslySetInnerHTML={{ __html: card.text }} />
                )}
                {card.bullets && card.bullets.length > 0 && (
                  <ul className="mt-4 space-y-2.5 text-black/80 text-sm leading-6 list-disc pl-5">
                    {card.bullets.map((b, j) => (
                      <li key={j} className="cms-html" dangerouslySetInnerHTML={{ __html: b }} />
                    ))}
                  </ul>
                )}
                {card.link && (
                  <div className="mt-4 text-center">
                    <Link to={card.link} className="text-[#2E8B57] font-bold text-sm hover:underline">Learn more…</Link>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* NEWS */}
      <section className="py-14 sm:py-20 bg-[#F6F7F9]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <SectionHeading subtitle={newsHeader.subtitle}>{newsHeader.title}</SectionHeading>
          <NewsCards limit={3} />
        </div>
      </section>
    </div>
  );
}
